import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LockClosedIcon,
  IdentificationIcon,
  PhoneIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { loginStudentByRegNo } from '../services/firebaseService';
import { checkStudentLoginEligibility } from '../services/examService';
import { StudentSession } from '../types';

const SESSION_KEY = 'kreed_class_session';

const ClassLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [regNo, setRegNo] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const session: StudentSession = JSON.parse(stored);
        if (Date.now() - session.loggedInAt < 8 * 60 * 60 * 1000) {
          navigate('/class/dashboard', { replace: true });
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedReg = regNo.trim().toUpperCase();
    if (!trimmedReg) {
      setError('Please enter your Registration ID (e.g. 001, 697, or full Registration Number).');
      return;
    }

    const cleanMobile = mobileNumber.replace(/\D/g, '');
    if (!cleanMobile) {
      setError('Please enter your registered mobile number.');
      return;
    }
    if (cleanMobile.length < 9 || cleanMobile.length > 10) {
      setError('Mobile number must be 9 or 10 digits.');
      return;
    }

    setLoading(true);
    try {
      const eligibility = await checkStudentLoginEligibility(false, trimmedReg);
      if (!eligibility.isAllowed) {
        setError(eligibility.message || 'The examination has officially concluded. Entry is now closed.');
        setLoading(false);
        return;
      }
      const session = await loginStudentByRegNo(trimmedReg, cleanMobile);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      navigate('/class/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-success-800 text-white flex flex-col justify-between selection:bg-warning-500 selection:text-white relative overflow-hidden">
      {/* Background Pattern / Floating Elements from Main Page */}
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Floating sports balls in background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 animate-bounce opacity-25">
          <div className="w-12 h-12 bg-warning-500/80 rounded-full flex items-center justify-center text-xl">⚽</div>
        </div>
        <div className="absolute top-20 right-16 animate-bounce delay-1000 opacity-25">
          <div className="w-10 h-10 bg-success-500/80 rounded-full flex items-center justify-center text-lg">🏸</div>
        </div>
        <div className="absolute bottom-20 left-16 animate-bounce delay-700 opacity-25">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-2xl">🏏</div>
        </div>
        <div className="absolute bottom-32 right-20 animate-bounce delay-500 opacity-25">
          <div className="w-12 h-12 bg-warning-500/80 rounded-full flex items-center justify-center text-xl">🏀</div>
        </div>
      </div>

      {/* Top Header Navbar */}
      <header className="relative z-10 w-full border-b border-white/15 bg-primary-950/40 backdrop-blur-md px-4 sm:px-8 py-3.5 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img 
            src="/logo192.png" 
            alt="Kreed Odisha Emblem" 
            className="w-10 h-10 sm:w-12 sm:h-12 object-contain drop-shadow"
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-base sm:text-lg tracking-tight text-white">
                Kreed Sahayak Portal
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-warning-500 text-primary-950">
                Phase 2
              </span>
            </div>
            <span className="text-[11px] sm:text-xs text-blue-100/80">
              Kreed Odisha Sports Excellence Programme
            </span>
          </div>
        </div>

        <a 
          href="/" 
          className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs sm:text-sm font-medium text-white transition-all border border-white/15 cursor-pointer"
        >
          <span>Main Site</span>
          <ChevronRightIcon className="w-3.5 h-3.5 text-warning-400" />
        </a>
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Main Title with Okreed Gradient */}
          <div className="text-center mb-6 sm:mb-8 animate-fade-in">
            <div className="inline-flex items-center space-x-1.5 px-3.5 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold mb-3 shadow-sm">
              <SparklesIcon className="w-3.5 h-3.5 text-warning-400" />
              <span>Interactive Live Training Portal</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              <span className="block text-white">Kreed Sahayak</span>
              <span className="block bg-gradient-to-r from-warning-400 to-success-300 bg-clip-text text-transparent">
                Student Classroom
              </span>
            </h1>
            <p className="text-xs sm:text-sm text-blue-100/90 mt-2 max-w-sm mx-auto leading-relaxed">
              Second Phase Online Training &amp; Real-Time Attendance Verification
            </p>
          </div>

          {/* Normal Classes Active Banner */}
          <div className="p-4 rounded-2xl border mb-5 shadow-xl backdrop-blur-md bg-emerald-950/80 border-emerald-500/50 text-emerald-100">
            <div className="flex items-center justify-between font-black text-xs sm:text-sm uppercase tracking-wider">
              <span className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping mr-1" />
                <span>🎓 Live Training &amp; Regular Classes</span>
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                CLASSES ACTIVE
              </span>
            </div>
            <p className="text-xs mt-1.5 leading-relaxed text-emerald-200/90">
              Regular Phase 2 training lectures and interactive classroom sessions are currently active. Please sign in below to attend your live session.
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white/15 backdrop-blur-xl border border-white/25 rounded-2xl shadow-2xl p-6 sm:p-8 relative">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-white/20">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center text-warning-300 shadow-sm">
                  <LockClosedIcon className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-bold text-white">Student Sign In</h2>
                  <p className="text-[11px] sm:text-xs text-blue-100">Authenticate with confirmation credentials</p>
                </div>
              </div>
              <ShieldCheckIcon className="w-5 h-5 text-success-300" />
            </div>

            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5" noValidate>
              {/* Registration Number Field */}
              <div>
                <label 
                  htmlFor="class-reg-no" 
                  className="block text-xs sm:text-sm font-semibold text-white mb-1.5"
                >
                  Candidate Reference / Registration ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-blue-200">
                    <IdentificationIcon className="w-5 h-5" />
                  </div>
                  <input
                    id="class-reg-no"
                    type="text"
                    placeholder="e.g. 001, 697, 0697, or KOP/2026/09/0001"
                    value={regNo}
                    onChange={(e) => setRegNo(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/25 rounded-xl text-white placeholder-blue-200/60 text-base font-mono focus:outline-none focus:ring-2 focus:ring-warning-400 focus:bg-white/15 transition-all"
                    autoComplete="off"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-[11px] text-blue-100/80 mt-1.5 flex items-center space-x-1">
                  <span>Enter the ID or registration number from your confirmation letter.</span>
                </p>
              </div>

              {/* Registered Mobile Field */}
              <div>
                <label 
                  htmlFor="class-mobile" 
                  className="block text-xs sm:text-sm font-semibold text-white mb-1.5"
                >
                  Registered Mobile Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-blue-200">
                    <PhoneIcon className="w-5 h-5" />
                  </div>
                  <input
                    id="class-mobile"
                    type="tel"
                    inputMode="numeric"
                    pattern="\d{9,10}"
                    maxLength={10}
                    placeholder="Enter registered mobile number"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/25 rounded-xl text-white placeholder-blue-200/60 text-base font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-warning-400 focus:bg-white/15 transition-all"
                    autoComplete="tel"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-[11px] text-blue-100/80 mt-1.5 flex items-center space-x-1">
                  <span>Enter the 9 or 10-digit mobile number submitted in your application.</span>
                </p>
              </div>

              {/* Error Notification */}
              {error && (
                <div className="flex items-start space-x-2.5 p-3 rounded-xl bg-red-600/30 border border-red-400/40 text-white text-xs sm:text-sm animate-fade-in shadow">
                  <ExclamationCircleIcon className="w-5 h-5 text-red-200 flex-shrink-0 mt-0.5" />
                  <span className="leading-snug">{error}</span>
                </div>
              )}

              {/* Submit Button - Always Enabled */}
              <button
                id="class-login-btn"
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 px-6 rounded-xl bg-warning-500 hover:bg-warning-600 active:scale-[0.98] text-white font-bold text-base shadow-xl shadow-warning-600/30 flex items-center justify-center space-x-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Verifying Credentials...</span>
                  </>
                ) : (
                  <>
                    <span>Enter Examination Hall</span>
                    <ArrowRightIcon className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Notice Footer inside card */}
            <div className="mt-5 pt-4 border-t border-white/20 text-center">
              <p className="text-[11px] sm:text-xs text-blue-100/90 leading-relaxed">
                🔔 Examination Active: <strong>30 Multiple Choice Questions &bull; 45 Minutes Assessment</strong>.
              </p>
            </div>
          </div>

          {/* 3 Step Indicator (Fully responsive on mobile) */}
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3 text-center">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2.5 sm:p-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-warning-500 text-primary-950 font-bold text-xs flex items-center justify-center mx-auto mb-1 shadow">
                1
              </div>
              <p className="text-[11px] sm:text-xs text-white font-medium leading-tight">Candidate ID</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2.5 sm:p-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-warning-500 text-primary-950 font-bold text-xs flex items-center justify-center mx-auto mb-1 shadow">
                2
              </div>
              <p className="text-[11px] sm:text-xs text-white font-medium leading-tight">Mobile No.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2.5 sm:p-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-success-500 text-white font-bold text-xs flex items-center justify-center mx-auto mb-1 shadow">
                3
              </div>
              <p className="text-[11px] sm:text-xs text-white font-medium leading-tight">Exam Hall</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-4 px-4 text-center border-t border-white/15 text-[11px] sm:text-xs text-blue-100/70">
        <p>
          &copy; {new Date().getFullYear()} Kreed Odisha Sports Excellence Programme &bull; Kreed Sahayak Portal &bull; okreed.in
        </p>
      </footer>
    </div>
  );
};

export default ClassLoginPage;